// Custom JSX runtime — zero dependencies
// Converts TSX → real DOM elements via h() function

type Props = Record<string, unknown> | null;
type Child = Node | string | number | boolean | null | undefined | Child[];

export function h(
  tag: string | ((props: Props & { children?: Child[] }) => HTMLElement),
  props: Props,
  ...children: Child[]
): HTMLElement {
  // Function component
  if (typeof tag === 'function') {
    return tag({ ...props, children });
  }

  const el = document.createElement(tag);

  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;

      if (key.startsWith('on') && typeof value === 'function') {
        // Event handler: onClick → click
        const event = key.slice(2).toLowerCase();
        el.addEventListener(event, value as EventListener);
      } else if (key === 'className') {
        el.className = String(value);
      } else if (key === 'style' && typeof value === 'object') {
        Object.assign(el.style, value);
      } else if (key === 'ref' && typeof value === 'function') {
        (value as (el: HTMLElement) => void)(el);
      } else if (value === true) {
        el.setAttribute(key, '');
      } else {
        el.setAttribute(key, String(value));
      }
    }
  }

  appendChildren(el, children);
  return el;
}

function appendChildren(el: HTMLElement, children: Child[]) {
  for (const child of children.flat(Infinity)) {
    if (child == null || child === false || child === true) continue;
    if (child instanceof Node) {
      el.appendChild(child);
    } else {
      el.appendChild(document.createTextNode(String(child)));
    }
  }
}

// Fragment support: <></> or <Fragment>
export function Fragment({ children }: { children?: Child[] }): HTMLElement {
  const frag = document.createElement('tmk-fragment');
  frag.style.display = 'contents';
  if (children) appendChildren(frag, children);
  return frag;
}

// JSX namespace for TypeScript
declare global {
  namespace JSX {
    type Element = HTMLElement;
    interface IntrinsicElements {
      [tag: string]: Record<string, unknown>;
    }
  }
}
